import * as React from 'react';
import * as TreeRepository from '../../Repository/TreeRepository';
import * as ContractRepository from '../../Repository/ContractRepository';
import TreeMaps from './TreeMaps';
import TreeTable from "./TreeTable";
import ErrorPage from "../ErrorPage/ErrorPage";
import merge from 'lodash/merge';
import { capitalize } from "../../Helper/DataHelper";
import MapPopup from "./Popup/MapPopup";
import ValidatedTrees from "./ValidatedTrees";
import { Affix, Alert, Col, message, Row } from 'antd';
import { default as Page, PageProps } from '../../Component/Page/Page';
import { Tree, Pager, GeoJsonFile } from 'tree-wallet-client';
import { GlobalContext } from '../../Contexts/GlobalContext';
import { isDesktopLayout } from '../../Helper/DeviceHelper';
import { userHasRole, UserRoles } from '../../Repository/UserRepository';
import { isAllowed, logout } from "../../Service/AuthService";
import { LngLatLike } from "mapbox-gl";
import Filters from './Filters/Filters';
import { plantingDay } from '../../Helper/DateHelper';
import { mergeTrees, readTreesListCopyPage, readTreesListCopyPlantedDays, isNativeApp } from '../../Dependency/NativeAppMemory';
import { readSavedCollection } from '../../Service/OfflineSaveService';
import { describeError, isOfflineError, OfflineError } from '../../Helper/ErrorHelper';
import { NO_NETWORK_CONNECTIVITY, genericError } from '../../Helper/BannerMessages';

export interface MapPosition {
  coordinates?: LngLatLike;
  treeId?: number;
  isInitial?: boolean;
  selected?: boolean;
  openPopup?: boolean;
  zoomLevel?: number;
}

interface ListPageState {
  pager?: MapPager;
  trees?: Tree[];
  /** Days (local `YYYY-MM-DD`) with at least one tree matching the applied filter */
  plantedDates?: string[];
  /** True while those days are being read: the Filters calendar spins until they arrive */
  plantedDatesLoading?: boolean;
  loading: boolean;
  filter: Filter;
  geoJsonFiles?: GeoJsonFile[] | undefined;
  showBanner: boolean;
  bannerMessage?: string;
  bannerType?: 'success' | 'info' | 'warning' | 'error';
  mapLoading: boolean;
  /** Set while a validation is being written to the validator's queue (see ValidatedTrees) */
  queueSaving: boolean;
  /** True while the map is stuck under the header because the tree list below is being scrolled */
  mapAffixed: boolean;
  /** True while the Filters panel is open: the map then starts under it */
  filtersPanelOpen: boolean;
  /** Height of the Filters strip, or of the panel while it is open (measured, it is content-sized) */
  filtersStripHeight: number;
  /** Trees list beside the map, as read from the viewport width (see `isDesktopLayout`) */
  desktopLayout: boolean;
}

export interface MapPager extends Pager {
  totalTreesPlanted?: number;
}

export interface Filter {
  sortField: string;
  sortOrder: string;
  contractId?: number;
  treeId?: number;
  validatorId?: number;
  zoomOut: boolean;
  dateStart?: number;
  dateEnd?: number;
}

export const allowedRoles = [
  ...Object.values(ContractRepository.roles),
  UserRoles.CompensationProgram
];

// Where the map sticks while the tree list is scrolled down
const MAP_AFFIX_OFFSET_TOP = 0;

export default class MapPage extends React.Component<PageProps, ListPageState> {
  static contextType = GlobalContext;
  declare context: React.ContextType<typeof GlobalContext>;
  declare mapRef: React.Ref<TreeMaps>;
  declare tableRef: React.Ref<TreeTable>;
  // The contract `plantedDates`, changed only when a new contract is filtered
  private plantedDatesContractId: number | null | undefined = undefined;
  // How many trees stand on each of those days, so a deletion only has to look at its own day
  private plantingDayCounts: { [day: string]: number } = {};
  private filtersRow: HTMLDivElement | null = null;
  private filtersStripObserver: ResizeObserver | null = null;

  /**
   * The filters `Save Offline` saved the trees list copy with: with no connection the map opens on that very list. Online
   * the panel keeps its own state, so browsing is not silently filtered by an old choice.
   */
  private static savedFilters(): Filter | null {
    if (!isNativeApp() || navigator.onLine) {
      return null;
    }

    return readSavedCollection()?.filter || null;
  }

  constructor(props: PageProps) {
    super(props);
    this.getTrees = this.getTrees.bind(this);
    this.refreshPlantedDates = this.refreshPlantedDates.bind(this);
    this.forgetPlantedDate = this.forgetPlantedDate.bind(this);
    this.updateState = this.updateState.bind(this);
    this.onDeleteTree = this.onDeleteTree.bind(this);
    this.mapRef = React.createRef();
    this.tableRef = React.createRef();
    this.state = {
      pager: {
        pageSize: 25,
        currentPage: 1,
      },
      trees: undefined,
      plantedDates: undefined,
      plantedDatesLoading: false,
      loading: true,
      filter: {
        zoomOut: true,
        sortField: 'createdAt',
        sortOrder: 'desc',
        contractId: undefined,
        treeId: undefined,
        validatorId: undefined,
        dateStart: undefined,
        dateEnd: undefined,
        ...(MapPage.savedFilters() || {}),
      },
      geoJsonFiles: undefined,
      showBanner: false,
      bannerMessage: undefined,
      bannerType: 'success',
      mapLoading: false,
      queueSaving: false,
      mapAffixed: false,
      filtersPanelOpen: false,
      filtersStripHeight: 0,
      desktopLayout: isDesktopLayout(),
    }
  }

  async componentDidMount() {
    const { contract, user } = this.context;

    try {
      // Check if error page "No contract defined" should be displayed
      if (!contract || !user) {
        return;
      }

      const geoJsonFilesList = await TreeRepository.getGeoJsons({
        params: { contractId: contract.id },
      });
      // getTrees method sets the state
      await this.getTrees(undefined, {
        ...this.state.filter,
      });
      // The map can start on its trees list data while the trees list copy is read: only the "Loading data..." waits for both
      this.setState({ geoJsonFiles: geoJsonFilesList.files });
    } catch (error) {
      // Every read above reports itself: what is left to do is to let the page settle
      console.error('could not open the map on the trees list data', { error });
    } finally {
      // Or "Loading data..." never ends
      this.setState({ loading: false });

      // A tablet turned from portrait to landscape crosses the breakpoint: the layout has to follow
      window.addEventListener('resize', this.syncLayout);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.syncLayout);
    this.filtersStripObserver?.disconnect();
    this.filtersStripObserver = null;
    document.body.style.removeProperty('--filters-strip-height');
  }

  private onMapLoadingChange = (isLoading: boolean) => {
    this.setState({ mapLoading: isLoading });
  };

  private syncLayout = () => {
    const desktopLayout = isDesktopLayout();
    if (desktopLayout !== this.state.desktopLayout) {
      this.setState({ desktopLayout });
    }
  };

  // A callback ref: the row is rendered only once the user and the contract are known
  private setFiltersRow = (row: HTMLDivElement | null) => {
    this.filtersStripObserver?.disconnect();
    this.filtersStripObserver = null;
    this.filtersRow = row;

    if (row && typeof ResizeObserver !== 'undefined') {
      this.filtersStripObserver = new ResizeObserver(this.syncFiltersStripHeight);
      this.filtersStripObserver.observe(row);
    }

    this.syncFiltersStripHeight();
  };

  // Published for two readers: the tabs and the column titles pin under that height (tree-map-table.less),
  // and while the panel is open the map's top starts on it. Measured because the open panel is content-sized
  private syncFiltersStripHeight = () => {
    const row = this.filtersRow;

    if (!row) {
      return;
    }

    const height = Math.round(row.getBoundingClientRect().height);

    if (height === this.state.filtersStripHeight) {
      return;
    }

    document.body.style.setProperty('--filters-strip-height', `${height}px`);
    this.setState({ filtersStripHeight: height });
  };

  private onFiltersToggle = (open: boolean) => this.setState({ filtersPanelOpen: open });

  // The planting days are read from trees list data - the contract's trees while the map is still loading
  private setPlantedDays = (days: { [day: string]: number }) => {
    this.plantedDatesContractId = this.state.filter.contractId ?? null;
    this.plantingDayCounts = days;
    this.setState({ plantedDates: Object.keys(days) });
  };

  async getTrees(params?: any, filter?: Filter, pager?: MapPager, errorCallback?: Function) {
    const { user } = this.context;
    const { filter: stateFilter, pager: statePager } = this.state;

    // Merge filters properly, with incoming filter taking precedence
    const compiledFilter = {
      ...stateFilter,
      ...(filter || {})
    };

    const compiledPager = merge(statePager, pager ? pager : {});

    console.log(compiledFilter);

    try {
      if (!user) {
        throw new Error('no user defined');
      }

      // No connection: in the app the trees copied on the last load answer the filter, a browser keeps nothing.
      // Either way the API is not asked, so the user reads one sentence instead of a failed request
      if (!navigator.onLine) {
        const cached = isNativeApp()
          ? await readTreesListCopyPage(compiledFilter, compiledPager?.currentPage, compiledPager?.pageSize)
          : null;

        if (!cached) {
          message.info(NO_NETWORK_CONNECTIVITY);
          throw new OfflineError();
        }

        const offlineResponse = {
          data: cached.data,
          pager: cached.pager,
          extraData: { treeCount: cached.treeCount },
        };

        this.setState({
          pager: offlineResponse.pager,
          trees: cached.data,
          filter: compiledFilter,
        });

        return offlineResponse;
      }

      const response = await TreeRepository.getTreeList({
        queryParams: {
          filter: JSON.stringify({
            ...compiledFilter,
            ...(params && params),
          }),
          pageSize: compiledPager?.pageSize,
          page: compiledPager?.currentPage,
        },
        admin: userHasRole(user.roles, UserRoles.Admin.name),
        errorCallback: (error: any) => {
          errorCallback && errorCallback(error);
        }
      });

      // IMPORTANT: Update the state with the compiled filter to persist it
      this.setState({
        pager: {
          ...response.pager,
          totalTreesPlanted: response.extraData.treeCount
        },
        trees: response.data,
        filter: compiledFilter // Persist the filter state
      });

      // Whatever the list shows is kept straight away: a contract filtered once already answers offline.
      // Not awaited: a slow disk must never hold the list back
      if (isNativeApp()) {
        void mergeTrees(response.data);
      }

      return response;
    } catch (error) {
      // The sentence offline is said where it is raised: reporting it here would show the same problem twice
      if (isOfflineError(error)) {
        throw error;
      }

      console.error('There was an error fetching the tree list: ' + describeError(error));
      message.error(genericError(describeError(error)));
      throw new Error('There was an error fetching the tree list');
    }
  }

  /**
   * Days (local `YYYY-MM-DD`) with at least one tree planted in the selected contract
   * `force`: re-read after the deletion of a tree
   */
  async refreshPlantedDates(filter?: Filter, force?: boolean) {
    const { user } = this.context;
    const appliedFilter = { ...this.state.filter, ...(filter || {}) };
    const contractId = appliedFilter.contractId ?? null;

    if (!user || (!force && contractId === this.plantedDatesContractId)) {
      return;
    }

    // Offline the same days are counted from the copied trees, without a request (app only)
    if (isNativeApp() && !navigator.onLine) {
      const cachedCounts = await readTreesListCopyPlantedDays({ contractId });

      this.plantedDatesContractId = contractId;
      this.plantingDayCounts = cachedCounts;
      this.setState({ plantedDates: Object.keys(cachedCounts) });
      return;
    }

    // The calendar shows its own spinner while planting days is still processing
    this.setState({ plantedDatesLoading: true });

    const contractFilter = {
      ...appliedFilter,
      treeId: null,
      validatorId: null,
      dateStart: null,
      dateEnd: null,
    };

    try {
      // How many trees the contract counts is not known yet - the list's pager counts the filtered rows,
      // which are at most as many. One row reads the total, then the whole set is asked for at once
      const first = await TreeRepository.getTreeList({
        queryParams: { filter: JSON.stringify(contractFilter), pageSize: 1, page: 1 },
        admin: userHasRole(user.roles, UserRoles.Admin.name),
      });

      const totalTrees = first?.pager?.totalRecords;

      if (totalTrees === undefined) {
        return;
      }

      const response = totalTrees > 1 ? await TreeRepository.getTreeList({
        queryParams: { filter: JSON.stringify(contractFilter), pageSize: totalTrees, page: 1 },
        admin: userHasRole(user.roles, UserRoles.Admin.name),
      }) : first;

      if (!response) {
        return;
      }

      const plantingDayCounts: { [day: string]: number } = {};

      (response.data || []).forEach((tree) => {
        if (tree.createdAt) {
          const day = plantingDay(tree.createdAt);
          plantingDayCounts[day] = (plantingDayCounts[day] || 0) + (tree.numberPlanted || 1);
        }
      });

      this.plantedDatesContractId = contractId;
      this.plantingDayCounts = plantingDayCounts;
      this.setState({ plantedDates: Object.keys(plantingDayCounts) });
    } catch (error) {
      console.error('could not gather the planting days to highlight', { error });
    } finally {
      this.setState({ plantedDatesLoading: false });
    }
  }

  /**
   * A deleted tree only concerns its own planting day: the day keeps its highlight in the Calendar 
   * while another tree(s) of the contract stands on it, and loses it when that tree was the last one
   */
  forgetPlantedDate(createdAt?: string, trees?: number) {
    if (!createdAt) {
      this.refreshPlantedDates(undefined, true);
      return;
    }

    const day = plantingDay(createdAt);
    const counts = { ...this.plantingDayCounts };

    if (!counts[day]) {
      return;
    }

    counts[day] -= trees || 1;

    if (counts[day] <= 0) {
      delete counts[day];
    }

    this.plantingDayCounts = counts;
    this.setState({ plantedDates: Object.keys(counts) });
  }

  getDefaultPosition(): MapPosition {
    const { trees } = this.state;
    if (trees === undefined || trees.length === 0) {
      return {
        coordinates: [0, 0]
      };
    }
    const tree = trees[trees.length - 1];
    return {
      coordinates: [
        tree.lng,
        tree.lat
      ],
    }
  }

  onDeleteTree(treeId: number) {
    const { trees } = this.state;
    this.setState({
      trees: trees && trees.filter(tree => tree.id !== treeId)
    });
  }

  updateState(stateData: any) {
    this.setState({
      ...stateData
    });
  }

  // Check if Filters panel should be shown based on user and contract roles
  private showsFiltersPanel(): boolean {
    const { user, contract } = this.context;

    return !!user && !!contract && (
      isAllowed(user.roles, [UserRoles.Admin, UserRoles.CompensationProgram]) ||
      isAllowed(contract.roles, [ContractRepository.roles.Validator, ContractRepository.roles.Agent, ContractRepository.roles.Coordinator])
    );
  }

  getTreemap() {
    const { filter, geoJsonFiles, loading } = this.state;
    const { contract } = this.context;

    if (!contract || !geoJsonFiles) {
      return null;
    }

    const { language } = contract.preferences;

    return <TreeMaps
      public={false}
      // While the tree list is scrolled under it the map is stuck, and then it holds the height of the
      // affix wrapper below instead of its own 85.5vh - see the `Affix` in the render
      fullHeight={this.state.mapAffixed}
      ref={this.mapRef}
      tableRef={this.tableRef}
      geoJsonFiles={geoJsonFiles}
      language={language}
      initialPosition={{ ...this.getDefaultPosition(), ...(filter.zoomOut ? { zoomLevel: 2 } : {}) }}
      template={MapPopup}
      pageLoading={loading}
      onTreeDeleted={this.forgetPlantedDate}
      onPlantingDays={this.showsFiltersPanel() ? this.setPlantedDays : undefined}
      onLoadingStateChange={this.onMapLoadingChange}
    />
  }

  render() {
    const { trees, loading, filter, pager } = this.state;
    const { user, contract } = this.context;
    const pageProps = { ...this.props, loading }

    // Debug: Log the filter being passed to Page
    console.log('MapPage render - current filter:', filter);

    if (!contract) {
      console.error('no contract defined');
      return <Page {...this.props} hideMenu={false} title={'Tree List'} allowedRoles={allowedRoles}>
        <ErrorPage template={"generic"} subTitle={'No contract defined'} buttonLabel="Logout" onButtonClick={logout} />
      </Page>
    }

    if (!user) {
      console.error('no user defined');
      return <Page {...this.props} hideMenu={false} title={'Tree List'} allowedRoles={allowedRoles}>
        <ErrorPage template={"generic"} subTitle={'No user defined'} />
      </Page>
    }

    const { language } = contract.preferences;
    const { desktopLayout } = this.state;

    return (
      <GlobalContext.Provider value={{ ...this.context, trees, filter, updateState: this.updateState }}>
        <Page {...pageProps} fullWidth title={capitalize(user.firstName)} allowedRoles={allowedRoles} filter={filter}>
          {this.state.showBanner && (
            <Alert
              message={this.state.bannerMessage || "Tree was added to queue"}
              type={this.state.bannerType || "success"}
              showIcon
              closable
              onClose={() => this.setState({ showBanner: false })}
              style={{ marginBottom: '16px' }}
            />
          )}
          {isAllowed(contract.roles, [ContractRepository.roles.Validator, ContractRepository.roles.Agent]) && (
            <Row justify='center' align='middle' className='validated-queue-strip'>
              <Col>
                <ValidatedTrees getTrees={this.getTrees} saving={this.state.queueSaving} />
              </Col>
            </Row>
          )}
          {this.showsFiltersPanel() &&
            <Row className={'map-filters-row'} ref={this.setFiltersRow}>
              <Col span={24}>
                <Filters
                  mapRef={this.mapRef}
                  filter={filter}
                  getTrees={this.getTrees}
                  updateState={this.updateState}
                  plantedDates={this.state.plantedDates}
                  plantedDatesLoading={this.state.plantedDatesLoading}
                  filteredTreeCount={this.state.pager?.totalTreesPlanted}
                  refreshPlantedDates={this.refreshPlantedDates}
                  onToggle={this.onFiltersToggle}
                  loading={this.state.mapLoading || loading}
                />
              </Col>
            </Row>
          }
          <Row>
            {/* No fixed breakpoint width here: while no tree is loaded the table
                renders nothing (or its "no data" placeholder), but a column sized
                by `md`/`xl` would still reserve a quarter of the row - and those
                breakpoints match a phone in landscape, leaving a blank strip on
                the left of the map. Sized by its content, the column collapses to
                zero width when the table is not rendered. */}
            <Col>
              {desktopLayout && (
                <TreeTable
                  mapRef={this.mapRef}
                  ref={this.tableRef}
                  language={language}
                  filter={filter}
                  trees={trees}
                  onDeleteTree={this.onDeleteTree}
                  getTrees={this.getTrees}
                  pagerData={pager}
                  updateState={this.updateState}
                  paginationConfig={{
                    defaultCurrent: pager?.currentPage,
                    pageSize: pager?.pageSize,
                    total: pager?.totalRecords,
                    pageSizeOptions: [25, 50, 100],
                    // 5 numbered tabs at most (antd shows 7 without it)
                    showLessItems: true,
                    showSizeChanger: true,
                  }}
                />
              )}
            </Col>
            <Col xs={24} xl={18} flex="auto" className='map-column'>
              <Affix
                // Closed panel: the map fills the window behind the pinned strip. Open panel: it waits
                // under the panel, which is as tall as its fields make it
                // Mobile: no offset at all
                offsetTop={desktopLayout ? (this.state.filtersPanelOpen ? this.state.filtersStripHeight : MAP_AFFIX_OFFSET_TOP) : undefined}
                onChange={(affixed) => this.setState({ mapAffixed: !!affixed })}
              >
                {/* A full map window right side of trees list */}
                <div className={'treemap-wrapper' + (this.state.mapAffixed ? ' affixed' : '')}>
                  {this.getTreemap()}
                </div>
              </Affix>
            </Col>
          </Row>
        </Page>
      </GlobalContext.Provider>
    );
  }
}
